import { styled as muiStyled } from '@mui/material/styles'
import Layout from '@/layouts/index'
import Page from '@/components/Page'
import pageService, { GlobalSetting, SeoMetadata, settingService } from '@/services/page'
import pathService from '@/services/path'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import {
  useTheme,
  Container,
  Grid,
  Stack,
  Typography,
  CircularProgress,
  Button,
  Skeleton,
  Checkbox,
} from '@mui/material'
import { RootStyle } from '@/sections/landing/Shares'
import { useVisitorFetchMidjourneyImagesLazyQuery } from '@/generated/graphql'
import Image from '@/components/Image'
import { useEffect, useMemo, useRef, useState } from 'react'
import EmptyContent from '@/components/EmptyContent'
import InfiniteScroll from 'react-infinite-scroll-component'
import { SkeletonImage } from '@/components/skeleton'
import CopyClipboard from '@/components/CopyClipboard'
import { LoadingButton } from '@mui/lab'
import { useRouter } from 'next/router'
import { ShopProductSearch } from '@/sections/e-commerce/shop'
import { PATH_APP_SERVICES } from '@/routes/paths'
import { fNumber } from '@/utils/formatNumber'
import ImageEditorDialogs from '@/components/ImageEditorDialogs'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface IndexProps {
  title?: string
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
}

const ContentStyle = muiStyled('div')(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
}))

MidjourneyImageCollectionPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

const IMAGES_PER_PAGE = 30

export default function MidjourneyImageCollectionPage({
  title,
  globalSetting,
  seoMetadataItemsCollection,
}: IndexProps) {
  const { locale, query } = useRouter()
  const theme = useTheme()
  const isFirstRun = useRef(true)
  const [isSelectEnabled, setIsSelectEnabled] = useState(false)
  const [selectedImagesId, setSelectedImagesId] = useState<string[]>([])
  const [
    visitorFetchMidjourneyImagesLazyQuery,
    {
      data: visitorFetchMidjourneyImagesQueryData,
      loading: visitorFetchMidjourneyImagesQueryLoading,
      fetchMore: visitorFetchMidjourneyImagesQueryFetchMore,
      refetch: visitorFetchMidjourneyImagesQueryRefetch,
    },
  ] = useVisitorFetchMidjourneyImagesLazyQuery({
    notifyOnNetworkStatusChange: true,
  })

  const [filter, setFilter] = useState<{
    search: string | null
    // sortBy: ProductsOrder | null
  }>({
    search: null,
    // sortBy: null,
  })

  const querySearch = useMemo<{
    search: string
    // sortBy: ProductsOrder
  }>(
    () => ({
      search: Array.isArray(query.search) ? '' : query.search ?? '',
      // sortBy: Array.isArray(query.sort)
      //   ? DEFAULT_PRODUCTS_ORDERS
      //   : query.sort
      //   ? Object.values(ProductsOrder).includes(query.sort as ProductsOrder)
      //     ? (query.sort as ProductsOrder)
      //     : DEFAULT_PRODUCTS_ORDERS
      //   : DEFAULT_PRODUCTS_ORDERS,
    }),
    [query]
  )

  const combinedFilter = useMemo<{
    search: string
    // sortBy: ProductsOrder
  }>(
    () => ({
      search: filter.search ?? querySearch.search,
      // tagIds: filter.tagIds ?? querySearch.tagIds,
      // sortBy: filter.sortBy ?? querySearch.sortBy,
    }),
    [filter, querySearch]
  )

  const images = visitorFetchMidjourneyImagesQueryData?.midjourneyImages ?? []

  useEffect(() => {
    visitorFetchMidjourneyImagesLazyQuery({
      variables: {
        offset: 0,
        limit: IMAGES_PER_PAGE,
        where: combinedFilter.search
          ? {
              prompt: {
                _ilike: `%${combinedFilter.search ?? ''}%`,
              },
            }
          : {},
      },
    })
  }, [combinedFilter])

  const handleFetchMore = () => {
    if (visitorFetchMidjourneyImagesQueryLoading) {
      return
    }
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }

    visitorFetchMidjourneyImagesQueryFetchMore({
      variables: {
        offset: images.length,
      },
    })
  }

  const totalImages =
    visitorFetchMidjourneyImagesQueryData?.midjourneyImages_aggregate?.aggregate?.count ?? 0

  return (
    <Page title={title ?? ''} metadataItems={seoMetadataItemsCollection}>
      <RootStyle theme={theme} backgroundColor="">
        <Container>
          <Grid container spacing={2} mb={5}>
            <Grid item xs={12} alignItems="center">
              <ShopProductSearch
                textFieldProps={{
                  fullWidth: true,
                  helperText: 'Search by prompt',
                }}
                value={combinedFilter.search}
                onSearch={(value) =>
                  setFilter((prev) => ({
                    ...prev,
                    search: value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <LoadingButton
                fullWidth
                size="large"
                sx={{ mb: 2 }}
                loading={visitorFetchMidjourneyImagesQueryLoading}
                variant="outlined"
                onClick={() => {
                  visitorFetchMidjourneyImagesQueryRefetch()
                }}
              >
                Refresh Data
              </LoadingButton>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1">
                {visitorFetchMidjourneyImagesQueryLoading ? (
                  <Skeleton variant="text" width="100px" height="30px" />
                ) : (
                  `${fNumber(totalImages)} images found`
                )}
              </Typography>
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center" justifyContent="end">
              {isSelectEnabled && (
                <Button
                  variant="outlined"
                  disabled={selectedImagesId.length < 1}
                  sx={{ mr: 3 }}
                  href={PATH_APP_SERVICES.midjourneyImageCollectionEdit(selectedImagesId)}
                >
                  Edit {selectedImagesId.length > 0 ? selectedImagesId.length : ''} Selected
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedImagesId([])
                  setIsSelectEnabled(!isSelectEnabled)
                }}
              >
                {isSelectEnabled ? 'Disabled Select' : 'Select More'}
              </Button>
            </Grid>
          </Grid>

          <ContentStyle>
            <InfiniteScroll
              key={combinedFilter.search}
              dataLength={images.length}
              next={handleFetchMore}
              scrollThreshold={0.8}
              hasMore={images.length < totalImages}
              loader={
                <Stack my={2} alignItems="center" justifyContent="center">
                  <CircularProgress color="primary" />
                </Stack>
              }
              endMessage={
                images.length >= totalImages && totalImages > IMAGES_PER_PAGE ? (
                  <Typography variant="h5" textAlign="center" sx={{ my: 2 }}>
                    Yay! You have seen it all
                  </Typography>
                ) : undefined
              }
            >
              <Grid container spacing={6}>
                {visitorFetchMidjourneyImagesQueryLoading
                  ? [...Array(12)].map((_, index) => <SkeletonImage key={index} />)
                  : images.map((image) => (
                      <Grid item key={image.id} xs={12} sm={6} md={4}>
                        <Grid container spacing={1}>
                          {isSelectEnabled && (
                            <Grid item xs={12} textAlign="center">
                              <Checkbox
                                checked={selectedImagesId.some((imageId) => imageId === image.id)}
                                onClick={() =>
                                  setSelectedImagesId(
                                    selectedImagesId.some((imageId) => imageId === image.id)
                                      ? selectedImagesId.filter((imageId) => imageId !== image.id)
                                      : [...selectedImagesId, image.id]
                                  )
                                }
                              />
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Image src={image.url} alt={image.name} />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="outlined"
                              fullWidth
                              href={PATH_APP_SERVICES.midjourneyImageCollectionSingleImageById(
                                image.id
                              )}
                            >
                              Detail Page
                            </Button>
                          </Grid>
                          {image.cloudinaryPublicId && (
                            <Grid item xs={12}>
                              <ImageEditorDialogs
                                cloudinaryPublicId={image.cloudinaryPublicId}
                                name={image.name}
                                contentType={image.contentType}
                              />
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <CopyClipboard shareInputValue={image.prompt ?? ''} />
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
              </Grid>
            </InfiniteScroll>
            {!visitorFetchMidjourneyImagesQueryLoading && images.length === 0 && (
              <Stack alignItems="center" justifyContent="center">
                <EmptyContent
                  title="No Data"
                  sx={{
                    '& span.MuiBox-root': { height: 160 },
                  }}
                />
              </Stack>
            )}
          </ContentStyle>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))
  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params
  const { getPage } = pageService(appId)
  const { getGlobalSetting } = settingService(appId)
  const [{ title, seoMetadataItemsCollection }, globalSetting] = await Promise.all([
    getPage('/', { locale }),
    getGlobalSetting({ locale }),
  ])

  return {
    props: {
      title: title ?? '',
      globalSetting,
      seoMetadataItemsCollection,
    },
    revalidate: 3600,
  }
}
